import { LayoutDashboard, Sparkles, History, Settings, Zap, LogOut, Shield, Users, CreditCard, Palette, Layers } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarHeader, SidebarFooter, useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useAppSettings } from "@/hooks/useAppSettings";
import { Button } from "@/components/ui/button";

const customerItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Generate SEO", url: "/generate", icon: Sparkles },
  { title: "Bulk Generate", url: "/bulk", icon: Layers },
  { title: "History", url: "/history", icon: History },
  { title: "Settings", url: "/settings", icon: Settings },
];

const adminItems = [
  { title: "Users", url: "/admin", icon: Users, end: true },
  { title: "Plans", url: "/admin/plans", icon: CreditCard },
  { title: "Brand", url: "/admin/brand", icon: Palette },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { pathname } = useLocation();
  const { user, signOut } = useAuth();
  const { isAdmin } = useIsAdmin();
  const { brand } = useAppSettings();

  const items = isAdmin ? adminItems : customerItems;
  const groupLabel = isAdmin ? "Admin" : "Workspace";
  const showLogo = !isAdmin && !!brand.logoUrl;
  const displayName = isAdmin ? "Admin Console" : brand.name || "StoreSEO AI";
  const displayTagline = isAdmin ? "Platform management" : brand.tagline || "E-commerce SEO";

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b">
        <NavLink to={isAdmin ? "/admin" : "/dashboard"} className="flex items-center gap-2 px-2 py-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-primary flex items-center justify-center shadow-glow shrink-0 overflow-hidden">
            {showLogo ? (
              <img src={brand.logoUrl} alt={displayName} className="h-full w-full object-cover" />
            ) : isAdmin ? (
              <Shield className="h-5 w-5 text-primary-foreground" />
            ) : (
              <Zap className="h-5 w-5 text-primary-foreground" />
            )}
          </div>
          {!collapsed && (
            <div className="flex flex-col leading-tight">
              <span className="font-bold text-sm">{displayName}</span>
              <span className="text-[10px] text-muted-foreground">{displayTagline}</span>
            </div>
          )}
        </NavLink>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{groupLabel}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item: any) => {
                const isActive = item.end
                  ? pathname === item.url
                  : pathname === item.url || pathname.startsWith(item.url + "/");
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive}>
                      <NavLink to={item.url} className="flex items-center gap-2">
                        <item.icon className="h-4 w-4" />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t">
        {!collapsed && user && (
          <div className="px-2 py-2 text-xs">
            <div className="font-medium truncate">{user.email}</div>
            {isAdmin && <div className="text-[10px] text-primary mt-0.5">Administrator</div>}
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={signOut}
          className="justify-start gap-2"
        >
          <LogOut className="h-4 w-4" />
          {!collapsed && "Sign out"}
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}
